import { useState, useCallback } from "react";
import PatternPreview from "../../components/PatternPreview";
import Symbol from "../../../../ui/Symbol/Symbol";
import Illustration from "./Illustration.svg?react";
import Background from "./Background.svg?react";
import styles from "./CreatePattern.module.scss";
import type { PatternTemplateData } from "./forms/PatternTemplate";
import PatternTemplate from "./forms/PatternTemplate";
import type { PatternArticleTypeData } from "./forms/PatternArticleType";
import PatternArticleType from "./forms/PatternArticleType";
import type { PatternArticleTitleData } from "./forms/PatternArticleTitle";
import PatternArticleTitle from "./forms/PatternArticleTitle";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import { Info } from "../../../../ui/Icon/Icon";

export type CreatePatternData = PatternTemplateData &
  PatternArticleTypeData &
  PatternArticleTitleData;

export type CreatePatternStep =
  | "ConfigureTemplate"
  | "ConfigureType"
  | "ConfigureTitle";

function CreatePatternForms({
  onSubmit,
  step,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nextStep,
  pattern,
}: {
  step: CreatePatternStep;
  nextStep: (step: CreatePatternStep, data: Partial<CreatePatternData>) => void;
  pattern: CreatePatternData;
  onSubmit: (data: CreatePatternData) => void;
}) {
  const onSubmitTemplate = useCallback(
    (data: PatternTemplateData) => {
      // nextStep("ConfigureType", data);
      onSubmit({
        ...pattern,
        ...data,
      });
    },
    [onSubmit, pattern],
  );

  const onSubmitType = useCallback(
    (data: PatternArticleTypeData) => {
      // nextStep("ConfigureTitle", data);
      onSubmit({
        ...pattern,
        ...data,
      });
    },
    [onSubmit, pattern],
  );

  const onSubmitTitle = useCallback(
    (data: PatternArticleTitleData) => {
      onSubmit({
        ...pattern,
        titleType: data.titleType,
      });
    },
    [onSubmit, pattern],
  );

  if (step === "ConfigureTemplate") {
    return (
      <PatternTemplate defaultValue={pattern} onSubmit={onSubmitTemplate} />
    );
  }

  if (step === "ConfigureType") {
    return (
      <PatternArticleType defaultValue={pattern} onSubmit={onSubmitType} />
    );
  }

  return (
    <PatternArticleTitle defaultValue={pattern} onSubmit={onSubmitTitle} />
  );
}

function CreatePattern({
  step,
  setStep,
  onSubmit,
  onNextStep,
  defaultData,
  onEditPattern,
}: {
  step: CreatePatternStep;
  setStep: React.Dispatch<React.SetStateAction<CreatePatternStep>>;
  onSubmit: (data: CreatePatternData) => void;
  onNextStep: (data: Partial<CreatePatternData>) => void;
  defaultData?: CreatePatternData;
  onEditPattern: () => void;
}) {
  const [pattern, setPattern] = useState<CreatePatternData>({
    templateNodes: [],
    symbols: [],
    type: undefined,
    titleType: "keyword",
    ...defaultData,
  });

  const goNextStep = useCallback(
    (nextStep: CreatePatternStep, nextPattern: Partial<CreatePatternData>) => {
      setStep(nextStep);
      setPattern((prev) => {
        return {
          ...prev,
          ...nextPattern,
          symbols: nextPattern.symbols
            ? nextPattern.symbols.map((symbol) => {
                return {
                  ...symbol,
                  values:
                    prev.symbols.find(
                      (prevSymbol) => prevSymbol.name === symbol.name,
                    )?.values ?? symbol.values,
                };
              })
            : prev.symbols,
        };
      });
      onNextStep(nextPattern);
    },
    [onNextStep, setStep],
  );

  return (
    <>
      {Boolean(pattern.templateNodes.length) &&
        step !== "ConfigureTemplate" && (
          <PatternPreview
            onEditPattern={onEditPattern}
            symbols={pattern.symbols}
            templateNodes={pattern.templateNodes}
            type={step !== "ConfigureType" ? pattern.type : undefined}
          />
        )}
      <div className={styles.container}>
        <div className={styles.header}>
          <Illustration />
          <h2 className={styles.heading}>
            <span>Create a new</span>
            <Symbol color="purple" name="Pattern" size="large" />
            <Tooltip
              content={
                <>
                  Patterns allows you to create thousands of programmatic
                  keyword variations very quickly.{" "}
                  <a
                    href="https://intercom.help/macaw-adfc0195d9d1/en/articles/9705587-getting-started-with-patterns"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Learn how to create a Pattern.
                  </a>
                </>
              }
            >
              <Info className={styles.infoIcon} />
            </Tooltip>
          </h2>
          <Background className={styles.background} />
        </div>
        <div className={styles.inputContainer}>
          <CreatePatternForms
            nextStep={goNextStep}
            onSubmit={onSubmit}
            pattern={pattern}
            step={step}
          />
        </div>
      </div>
    </>
  );
}

export default CreatePattern;
